import { useFragment } from "react-relay";
import { match } from "ts-pattern";
import { isRunningAgainstProduction } from "../../../environment";
import { externalProviderLinkClass } from "@features/orders/external-provider-link";
import { isOrderPaid } from "@features/orders/order-paid-status";
import { useTranslation } from "@i18n/hooks";
import { type paymentProviderLink_OrderFragment$key } from "@relay/paymentProviderLink_OrderFragment.graphql";
import { ORDER_FRAGMENT } from "./payment-provider-link.graphql";
import { type PaymentProviderLinkProps } from "./payment-provider-link.types";

export const PaymentProviderLink = ({ orderFragmentRef }: PaymentProviderLinkProps) => {
	const { t } = useTranslation("common");
	const order = useFragment<paymentProviderLink_OrderFragment$key>(
		ORDER_FRAGMENT,
		orderFragmentRef,
	);
	const isProduction = isRunningAgainstProduction();

	const isPaid = isOrderPaid(order.status);

	// The provider/method is known as soon as paymentData exists (from HasPaymentDetails
	// onward), well before the order is actually paid. PayPal in particular sits in
	// PaymentInProcess while its capture webhook is pending (backend's
	// OrderStatusType.StatusWherePaypalWebhookCanFinalize = HasPaymentDetails + PaymentInProcess)
	// - gating the whole column on isPaid hid the provider entirely during that window (showed a
	// generic "Unpaid" instead of "PayPal"). So show the provider whenever we know it.
	//
	// This component also owns the merged Payment Method column (Ali's call - the two used to be
	// separate columns showing near-duplicate info). Before paymentData exists at all - the buyer
	// has chosen a method but no payment attempt has happened yet - fall back to just the chosen
	// method name from selectedPaymentMethod; before even that, there's nothing to show.
	if (!order.paymentData) {
		if (order.selectedPaymentMethod?.paymentMethodType) {
			return <>{t(`paymentMethod.${order.selectedPaymentMethod.paymentMethodType}`)}</>;
		}
		return <>{t("order.unpaid")}</>;
	}

	const paymentString = t(`paymentMethod.${order.paymentData.paymentMethod}`);

	const providerNode = match(order.paymentData.dataType)
		.with("Stripe", "StripeSepa", () => {
			const paymentIntentId = order.paymentData?.paymentIntentId;
			if (isProduction && paymentIntentId) {
				return (
					<a
						className={externalProviderLinkClass}
						rel="noopener noreferrer"
						target="_blank"
						href={`https://dashboard.stripe.com/payments/${paymentIntentId}`}
					>
						{paymentString}
					</a>
				);
			}
			if (paymentIntentId) {
				return (
					<a
						className={externalProviderLinkClass}
						rel="noopener noreferrer"
						target="_blank"
						href={`https://dashboard.stripe.com/test/payments/${paymentIntentId}`}
					>
						{paymentString}
					</a>
				);
			}
			return <>{paymentString}</>;
		})
		.with("Paypal", () => {
			// No confirmed PayPal dashboard URL pattern (unlike Stripe's well-known
			// /customers/{id}), so surface the payer id as plain, copyable text for now
			// rather than link to something possibly wrong.
			const payerId = order.paymentData?.payerId;
			if (!payerId) return <>{paymentString}</>;
			return (
				<>
					{paymentString} ({t("order.payerId")}: {payerId})
				</>
			);
		})
		.otherwise(() => <>{paymentString}</>);

	if (isPaid) {
		return providerNode;
	}

	return (
		<>
			{providerNode} ({t("order.paymentPending")})
		</>
	);
};
